import { get, post } from './httpClient';

/**
 * Alta self-service de una lista CAD: presigned URL → PUT a S3 → parse →
 * confirmación. Contrato: `argenlotapi/docs/frontend/FRONTEND_CAD_UPLOAD.md`.
 * El front **no** parsea el DXF ni manda el GeoJSON del preview al confirmar.
 *
 * @see docs/maps/cad-upload.md
 */

/** El parse lee un DXF de hasta 15 MB: más lento que un GET normal. */
const PARSE_TIMEOUT_MS = 120000;
/** El PUT sube el archivo entero; sin tope de la Lambda. */
const UPLOAD_TIMEOUT_MS = 180000;

function isAbortError(error) {
  return error?.name === 'AbortError';
}

function readApiError(response, fallback) {
  const text = response?.error || response?.message;
  const trimmed = typeof text === 'string' ? text.trim() : '';
  return trimmed || fallback;
}

function withStatus(payload, response) {
  return {
    ...payload,
    _httpStatus: response?._httpStatus ?? null,
  };
}

/**
 * GET /getcaduploadurl?filename=. JWT. El dueño es el JWT; no pide empresa.
 * Devuelve `job_id`, la URL prefirmada y el `contentType` que **tiene que**
 * mandar el PUT: si no coincide, S3 responde `SignatureDoesNotMatch`.
 * 400 si no es `.dxf`. `company_id` se ignora si viene: no lo mandes.
 *
 * @param {{ filename: string }} params
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{
 *   ok: boolean,
 *   source: 'api'|'none',
 *   upload: object|null,
 *   message?: string,
 *   _httpStatus: number|null,
 * }>}
 */
export async function getCadUploadUrl({ filename } = {}, { signal } = {}) {
  const trimmed = typeof filename === 'string' ? filename.trim() : '';
  if (!trimmed) {
    return {
      ok: false,
      source: 'none',
      upload: null,
      message: 'Elegí un archivo para subir.',
      _httpStatus: null,
    };
  }
  try {
    const response = await get('/getcaduploadurl', {
      params: { filename: trimmed },
      signal,
    });
    if (response._httpOk && response.data) {
      return withStatus({ ok: true, source: 'api', upload: response.data }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      upload: null,
      message: readApiError(response, 'No se pudo preparar la carga del plano.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD upload: GET /getcaduploadurl falló', error);
    return {
      ok: false,
      source: 'none',
      upload: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * PUT del archivo crudo a la URL prefirmada. No pasa por la Lambda ni lleva
 * `Authorization`: la firma ya autoriza. El `Content-Type` es exactamente el
 * que devolvió `/getcaduploadurl` (default `application/octet-stream`);
 * cambiarlo rompe la firma (`SignatureDoesNotMatch`).
 *
 * Único `fetch` directo del proyecto: httpClient serializa el body a JSON y
 * apunta a `API_BASE_URL`, y acá se sube un binario a S3.
 *
 * @param {{ presignedUrl: string, file: File|Blob, contentType?: string }} params
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, message?: string, _httpStatus: number|null }>}
 */
export async function putCadFileToS3({ presignedUrl, file, contentType } = {}, { signal } = {}) {
  if (!presignedUrl || !file) {
    return { ok: false, message: 'Falta la URL de carga o el archivo.', _httpStatus: null };
  }

  const controller = new AbortController();
  const onExternalAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onExternalAbort);
  }
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, UPLOAD_TIMEOUT_MS);

  try {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType || 'application/octet-stream' },
      body: file,
      signal: controller.signal,
    });
    if (response.ok) {
      return { ok: true, _httpStatus: response.status };
    }
    return {
      ok: false,
      message: 'No se pudo subir el archivo. Probá de nuevo.',
      _httpStatus: response.status,
    };
  } catch (error) {
    if (timedOut) {
      return {
        ok: false,
        message: 'La subida tardó demasiado. Probá con una conexión más estable.',
        _httpStatus: null,
      };
    }
    if (isAbortError(error)) throw error;
    console.error('CAD upload: PUT a S3 falló', error);
    return {
      ok: false,
      message: 'No se pudo subir el archivo.',
      _httpStatus: null,
    };
  } finally {
    clearTimeout(timeoutId);
    if (signal) signal.removeEventListener('abort', onExternalAbort);
  }
}

/**
 * POST /parsecadproject. JWT. Devuelve el preview (`display_mode: canvas`,
 * `srid: 0`) y el informe del plano. Los `Feature.id` son de preview y
 * **cambian** al confirmar. 400 = el archivo no sirve y no se escribió nada.
 *
 * @param {{ jobId: string, filename?: string, layer?: string }} body
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{
 *   ok: boolean,
 *   source: 'api'|'none',
 *   preview: object|null,
 *   message?: string,
 *   _httpStatus: number|null,
 * }>}
 */
export async function parseCadProject({ jobId, filename, layer } = {}, { signal } = {}) {
  const trimmedJob = typeof jobId === 'string' ? jobId.trim() : '';
  if (!trimmedJob) {
    return {
      ok: false,
      source: 'none',
      preview: null,
      message: 'Subí el plano antes de continuar.',
      _httpStatus: null,
    };
  }
  const body = { job_id: trimmedJob };
  if (typeof filename === 'string' && filename.trim()) body.filename = filename.trim();
  if (typeof layer === 'string' && layer.trim()) body.layer = layer.trim();

  try {
    const response = await post('/parsecadproject', {
      body,
      signal,
      timeoutMs: PARSE_TIMEOUT_MS,
    });
    if (response._httpOk && response.data) {
      return withStatus({ ok: true, source: 'api', preview: response.data }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      preview: null,
      message: readApiError(response, 'No se pudo leer el plano.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD upload: POST /parsecadproject falló', error);
    return {
      ok: false,
      source: 'none',
      preview: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}

/**
 * POST /createcadproject. JWT. Confirma el preview que dejó el parse en S3:
 * solo `job_id` + `name` (nunca el GeoJSON del canvas). Crea la lista con
 * `is_shared: false`, `display_mode: canvas` y `company_id: null`.
 * El dueño es el JWT. No publica ni georreferencia.
 *
 * @param {{ jobId: string, name: string, slug?: string }} body
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{
 *   ok: boolean,
 *   source: 'api'|'none',
 *   project: object|null,
 *   message?: string,
 *   _httpStatus: number|null,
 * }>}
 */
export async function createCadProject({ jobId, name, slug } = {}, { signal } = {}) {
  const trimmedJob = typeof jobId === 'string' ? jobId.trim() : '';
  const trimmedName = typeof name === 'string' ? name.trim() : '';
  if (!trimmedJob || !trimmedName) {
    return {
      ok: false,
      source: 'none',
      project: null,
      message: 'Falta el nombre de la lista.',
      _httpStatus: null,
    };
  }
  const body = { job_id: trimmedJob, name: trimmedName };
  if (typeof slug === 'string' && slug.trim()) body.slug = slug.trim();

  try {
    const response = await post('/createcadproject', {
      body,
      signal,
      timeoutMs: PARSE_TIMEOUT_MS,
    });
    if (response._httpOk && response.data) {
      return withStatus({ ok: true, source: 'api', project: response.data }, response);
    }
    return withStatus({
      ok: false,
      source: 'api',
      project: null,
      message: readApiError(response, 'No se pudo crear la lista.'),
    }, response);
  } catch (error) {
    if (isAbortError(error)) throw error;
    console.error('CAD upload: POST /createcadproject falló', error);
    return {
      ok: false,
      source: 'none',
      project: null,
      message: 'No se pudo hablar con el servidor.',
      _httpStatus: null,
    };
  }
}
